﻿import type { ModuleType, DimensionType, EducationLevel, ExperienceLevel, RiskLevel, JobOutlook } from "../types";

// --- 模块配置 ---
export const MODULE_CONFIG: Record<ModuleType, { label: string; description: string; required: boolean }> = {
  A: {
    label: "职业与行业定位",
    description: "了解你的职业背景，匹配研究数据库",
    required: true,
  },
  B: {
    label: "工作任务构成",
    description: "评估你的日常工作中有多少内容可被 AI 加速 — 核心评分模块",
    required: false,
  },
  C: {
    label: "技能与学习画像",
    description: "了解你已具备的 AI 时代增值技能",
    required: false,
  },
  D: {
    label: "行业与组织环境",
    description: "评估你所在行业和公司的 AI 冲击程度",
    required: false,
  },
  E: {
    label: "适应力与心态",
    description: "心态和适应能力决定你在变革中的主动权",
    required: false,
  },
  V: {
    label: "一致性校验",
    description: "",
    required: false,
  },
};

export const MODULE_ORDER: ModuleType[] = ["A", "B", "C", "D", "E"];

// --- 评分维度权重 ---
export const DIMENSION_WEIGHTS: Record<DimensionType, { weight: number; label: string }> = {
  task_exposure:       { weight: 0.30, label: "任务暴露度" },
  skill_vulnerability:  { weight: 0.25, label: "技能脆弱度" },
  industry_impact:     { weight: 0.20, label: "行业冲击度" },
  adaptability:        { weight: 0.15, label: "适应弹性" },
  outlook:             { weight: 0.10, label: "发展前景" },
};

// --- 风险等级 ---
export const RISK_LEVELS: Record<RiskLevel, { label: string; color: string; min: number; max: number; meaning: string }> = {
  low:        { label: "低风险",   color: "#22c55e", min: 0,  max: 25, meaning: "你的职业在 AI 时代相对安全，继续保持学习和关注" },
  medium_low: { label: "中低风险", color: "#3b82f6", min: 26, max: 45, meaning: "有一定影响但可控，建议主动学习 AI 工具" },
  medium:     { label: "中等风险", color: "#eab308", min: 46, max: 60, meaning: "需要认真对待，建议制定技能提升计划" },
  high:       { label: "高风险",   color: "#f97316", min: 61, max: 75, meaning: "你的岗位可能在 3-5 年内发生重大变化，需要尽快行动" },
  extreme:    { label: "极高风险", color: "#ef4444", min: 76, max: 100, meaning: "你的岗位面临较大的 AI 替代压力，建议立即制定职业提升计划" },
};

// --- 30 个职业大类 (来源: AI调查报告 A1 题) ---
export interface OccupationCategory {
  code: string;
  name: string;
  onet_prefix: string;
  outlook: JobOutlook;
  outlook_score: number;  // D5 预设值
  examples: string;
}

export const OCCUPATION_CATEGORIES: OccupationCategory[] = [
  { code: "01", name: "管理和高管",          onet_prefix: "11-xxxx", outlook: "stable",     outlook_score: 45, examples: "CEO、总监、部门经理" },
  { code: "02", name: "商业和金融",          onet_prefix: "13-xxxx", outlook: "transition", outlook_score: 60, examples: "会计、审计、财务分析、投资" },
  { code: "03", name: "计算机和信息技术",    onet_prefix: "15-xxxx", outlook: "growth",     outlook_score: 20, examples: "软件开发、数据分析、网络安全" },
  { code: "04", name: "建筑和工程",          onet_prefix: "17-xxxx", outlook: "stable",     outlook_score: 45, examples: "建筑师、土木工程师、电气工程" },
  { code: "05", name: "生命/物理/社会科学",  onet_prefix: "19-xxxx", outlook: "stable",     outlook_score: 40, examples: "生物学家、化学家、经济学家" },
  { code: "06", name: "社区和社会服务",      onet_prefix: "21-xxxx", outlook: "stable",     outlook_score: 50, examples: "社工、心理咨询师" },
  { code: "07", name: "法律",               onet_prefix: "23-xxxx", outlook: "stable",     outlook_score: 45, examples: "律师、法官、法律助理" },
  { code: "08", name: "教育和培训",          onet_prefix: "25-xxxx", outlook: "growth",     outlook_score: 30, examples: "教师、教授、培训师" },
  { code: "09", name: "艺术/设计/娱乐/媒体", onet_prefix: "27-xxxx", outlook: "transition", outlook_score: 55, examples: "作家、设计师、记者、摄影师" },
  { code: "10", name: "医疗诊断和治疗",      onet_prefix: "29-1xxx", outlook: "growth",     outlook_score: 25, examples: "医生、牙医、药剂师" },
  { code: "11", name: "医疗技术和辅助",      onet_prefix: "29-2xxx", outlook: "growth",     outlook_score: 25, examples: "护士、医学影像技师" },
  { code: "12", name: "保健支持",            onet_prefix: "31-xxxx", outlook: "growth",     outlook_score: 30, examples: "护理员、理疗助理" },
  { code: "13", name: "安保和执法",          onet_prefix: "33-xxxx", outlook: "stable",     outlook_score: 50, examples: "警察、消防员、安保人员" },
  { code: "14", name: "餐饮服务",            onet_prefix: "35-xxxx", outlook: "stable",     outlook_score: 50, examples: "厨师、服务员、调酒师" },
  { code: "15", name: "个人护理和服务",      onet_prefix: "39-xxxx", outlook: "stable",     outlook_score: 45, examples: "美容师、健身教练" },
  { code: "16", name: "销售",               onet_prefix: "41-xxxx", outlook: "transition", outlook_score: 60, examples: "零售销售、房产中介、保险代理" },
  { code: "17", name: "行政和办公支持",      onet_prefix: "43-xxxx", outlook: "decline",    outlook_score: 80, examples: "行政助理、文员、客服" },
  { code: "18", name: "农林牧渔",            onet_prefix: "45-xxxx", outlook: "decline",    outlook_score: 75, examples: "农民、渔民、林业工人" },
  { code: "19", name: "建筑施工和开采",      onet_prefix: "47-xxxx", outlook: "stable",     outlook_score: 50, examples: "建筑工人、电工、管道工" },
  { code: "20", name: "设备安装和维修",      onet_prefix: "49-xxxx", outlook: "stable",     outlook_score: 50, examples: "机械维修、电器维修" },
  { code: "21", name: "生产制造",            onet_prefix: "51-xxxx", outlook: "decline",    outlook_score: 70, examples: "流水线操作、质检、焊接" },
  { code: "22", name: "运输和物流",          onet_prefix: "53-xxxx", outlook: "transition", outlook_score: 60, examples: "司机、快递、仓储" },
  { code: "23", name: "翻译和语言服务",      onet_prefix: "27-3091", outlook: "decline",    outlook_score: 85, examples: "翻译、口译" },
  { code: "24", name: "市场营销和公关",      onet_prefix: "11-2xxx", outlook: "transition", outlook_score: 60, examples: "市场专员、公关、品牌策划" },
  { code: "25", name: "人力资源",            onet_prefix: "13-107x", outlook: "transition", outlook_score: 55, examples: "HR专员、招聘、培训" },
  { code: "26", name: "研究和数据分析",      onet_prefix: "15-2xxx", outlook: "growth",     outlook_score: 20, examples: "研究员、统计分析、数据科学家" },
  { code: "27", name: "创业/自由职业",       onet_prefix: "--",      outlook: "transition", outlook_score: 55, examples: "自雇者、自媒体、独立咨询" },
  { code: "28", name: "学生（待就业）",      onet_prefix: "--",      outlook: "transition", outlook_score: 50, examples: "在校学生" },
  { code: "29", name: "政府/公共服务",       onet_prefix: "--",      outlook: "stable",     outlook_score: 40, examples: "公务员、事业单位" },
  { code: "30", name: "其他",               onet_prefix: "--",      outlook: "transition", outlook_score: 55, examples: "未在以上分类中" },
];

// --- 15 个行业 + 冲击分预设 (来源: AI调查报告 A2 题 + McKinsey + WEF) ---
export interface IndustryEntry {
  code: string;
  name: string;
  impact_score: number;  // 0-100
}

export const INDUSTRIES: IndustryEntry[] = [
  { code: "01", name: "互联网/科技",       impact_score: 85 },
  { code: "02", name: "金融/银行/保险",    impact_score: 80 },
  { code: "03", name: "零售/电商/消费品",  impact_score: 70 },
  { code: "04", name: "医疗/制药/生命科学", impact_score: 60 },
  { code: "05", name: "教育/培训",         impact_score: 50 },
  { code: "06", name: "制造业",            impact_score: 45 },
  { code: "07", name: "能源/采矿",         impact_score: 40 },
  { code: "08", name: "房地产/建筑",       impact_score: 35 },
  { code: "09", name: "政府/公共服务",     impact_score: 30 },
  { code: "10", name: "农业/林业/渔业",    impact_score: 20 },
  { code: "11", name: "传媒/出版/广告",    impact_score: 80 },
  { code: "12", name: "法律/咨询",         impact_score: 70 },
  { code: "13", name: "物流/运输",         impact_score: 45 },
  { code: "14", name: "餐饮/酒店/旅游",    impact_score: 30 },
  { code: "15", name: "其他",              impact_score: 50 },
];

// --- 学历调整因子 (来源: OpenAI Table 6, Job Zone beta 暴露度) ---
export const EDUCATION_ADJUSTMENT: Record<EducationLevel, { label: string; factor: number }> = {
  junior_high:  { label: "初中及以下",     factor: 0.60 },
  high_school:  { label: "高中/中专/技校", factor: 0.75 },
  associate:    { label: "大专/高职",      factor: 0.90 },
  bachelor:     { label: "本科",           factor: 1.10 },
  master_plus:  { label: "硕士及以上",     factor: 1.05 },
};

// --- 经验调整因子 (来源: OpenAI Table 7, 在职培训与暴露度反比) ---
export const EXPERIENCE_ADJUSTMENT: Record<ExperienceLevel, { label: string; factor: number }> = {
  under_1yr:  { label: "不到 1 年", factor: 1.15 },
  "1_3yr":    { label: "1-3 年",   factor: 1.10 },
  "3_5yr":    { label: "3-5 年",   factor: 1.00 },
  "5_10yr":   { label: "5-10 年",  factor: 0.95 },
  over_10yr:  { label: "10 年以上", factor: 0.90 },
};
